import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Clock, RefreshCw, Loader2, Plus, Trash2, Play, Save,
  CheckCircle2, AlertCircle, ChevronDown, ChevronRight, Calendar, Database, FileCheck, ShoppingCart,
  FileSpreadsheet, Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${API_URL}/api/admin/tareas-programadas`;

const PROCESS_META = {
  'yiqi-import': { label: 'Importación YIQI', icon: Database, multi: true },
  'aprobacion-cotizaciones': { label: 'Aprobación de Cotizaciones', icon: FileCheck, multi: true },
  'ordenes-compra': { label: 'Órdenes de Compra', icon: ShoppingCart, multi: true },
  'conciliacion-arca': { label: 'Conciliación ARCA', icon: FileSpreadsheet, multi: true },
  // La cartera es un saldo: con una foto por día alcanza. Admite más de un
  // horario porque la captura es idempotente, así que una segunda corrida
  // reescribe la foto del mismo día con datos más frescos.
  'cartera': { label: 'Cartera (deuda y cheques)', icon: Wallet, multi: true },
  'panel-diario-snapshot': { label: 'Snapshot del Panel Diario', icon: Calendar, multi: true },
};

const STATUS_STYLE = {
  ok:      { label: 'OK',        cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  partial: { label: 'Parcial',   cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  error:   { label: 'Error',     cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  running: { label: 'En curso',  cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { label: status || '—', cls: 'bg-gray-700 text-gray-400 border-gray-600' };
  return <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
};

// ── Tarjeta de configuración de un proceso ───────────────────────────────────
const ProcessCard = ({ process, data, token, onSaved, onRan }) => {
  const meta = PROCESS_META[process];
  const Icon = meta.icon;
  const [enabled, setEnabled] = useState(!!data.enabled);
  const [times, setTimes] = useState(data.times && data.times.length ? data.times : (meta.multi ? [] : ['']));
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    setEnabled(!!data.enabled);
    setTimes(data.times && data.times.length ? data.times : (meta.multi ? [] : ['']));
  }, [data, meta.multi]);

  const setTimeAt = (i, v) => setTimes(t => t.map((x, idx) => idx === i ? v : x));
  const addTime = () => setTimes(t => [...t, '']);
  const removeTime = (i) => setTimes(t => t.filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = times.filter(Boolean);
    setSaving(true);
    try {
      const resp = await fetch(`${BASE}/${process}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, times: clean }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || 'Error al guardar');
      toast.success(`${meta.label}: configuración guardada`);
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const resp = await fetch(`${BASE}/${process}/run-now`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || 'Error al ejecutar');
      toast.success(`${meta.label}: ejecución iniciada`);
      setTimeout(onRan, 1500);
    } catch (e) { toast.error(e.message); }
    finally { setRunning(false); }
  };

  return (
    <div className="bg-[#1A1D27] border border-gray-800 rounded-xl p-5 shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#00a1df]/10 border border-[#00a1df]/20">
            <Icon className="w-5 h-5 text-[#00a1df]" />
          </div>
          <div>
            <h3 className="text-sm font-black text-white">{meta.label}</h3>
            <p className="text-[11px] text-gray-500">
              {meta.multi ? 'Varios horarios por día' : 'Un horario por día'}
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className="text-[10px] font-bold uppercase text-gray-500">{enabled ? 'Activo' : 'Inactivo'}</span>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-[#00a1df]" />
        </label>
      </div>

      <div className="space-y-2 mb-4">
        <label className="text-[10px] font-bold uppercase text-gray-500">Horario(s) de ejecución</label>
        {(meta.multi ? times : times.slice(0, 1)).map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="time" value={t || ''} onChange={e => setTimeAt(i, e.target.value)}
              className="bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00a1df]" />
            {meta.multi && (
              <button onClick={() => removeTime(i)}
                className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all" title="Quitar">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {meta.multi && (
          <button onClick={addTime}
            className="flex items-center gap-1.5 text-xs font-bold text-[#00a1df] hover:text-[#33b5e6] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Agregar horario
          </button>
        )}
        {meta.multi && times.length === 0 && (
          <p className="text-[11px] text-gray-600">Sin horarios. Agregá al menos uno y activá el proceso.</p>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-800 pt-3">
        <div className="text-[11px] text-gray-500">
          <div>Próximo: <span className="text-gray-300">{fmt(data.next_run_at)}</span></div>
          <div className="flex items-center gap-1.5 mt-0.5">
            Última: <span className="text-gray-300">{fmt(data.last_run_at)}</span>
            {data.last_status && <StatusBadge status={data.last_status} />}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runNow} disabled={running}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-all disabled:opacity-50">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Ejecutar ahora
          </button>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-[#00a1df] hover:bg-[#0091ca] rounded-lg shadow-lg transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Fila del historial (expandible con el log completo) ──────────────────────
const RunRow = ({ run, token }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const meta = PROCESS_META[run.process] || { label: run.process };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      try {
        const resp = await fetch(`${BASE}/runs/${run.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const d = await resp.json();
        if (!resp.ok) throw new Error(d.detail || 'Error al cargar el detalle');
        setDetail(d);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    }
  };

  return (
    <>
      <tr onClick={toggle} className="border-b border-gray-800/60 hover:bg-gray-800/20 transition-colors cursor-pointer">
        <td className="px-4 py-3 text-gray-500">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{fmt(run.started_at)}</td>
        <td className="px-4 py-3 text-sm font-semibold text-white">{meta.label}</td>
        <td className="px-4 py-3">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${run.trigger === 'manual' ? 'bg-purple-500/15 text-purple-300 border-purple-500/30' : 'bg-gray-700/40 text-gray-400 border-gray-600'}`}>
            {run.trigger === 'manual' ? 'Manual' : 'Cron'}
          </span>
        </td>
        <td className="px-4 py-3 text-[11px] font-mono text-gray-400">{run.cron || '—'}{run.scheduled_time ? ` (${run.scheduled_time})` : ''}</td>
        <td className="px-4 py-3"><StatusBadge status={run.status} /></td>
        <td className="px-4 py-3 text-xs text-gray-400 max-w-[280px] truncate">{run.summary || '—'}</td>
      </tr>
      {open && (
        <tr className="bg-[#0F1117]">
          <td colSpan={7} className="px-6 py-4">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando log...</div>
            ) : detail ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4 text-[11px] text-gray-500">
                  <span>Inicio: <span className="text-gray-300">{fmt(detail.started_at)}</span></span>
                  <span>Fin: <span className="text-gray-300">{fmt(detail.finished_at)}</span></span>
                  <span>Duración: <span className="text-gray-300">{detail.duration_seconds != null ? `${detail.duration_seconds}s` : '—'}</span></span>
                  {detail.counts && Object.keys(detail.counts).length > 0 && (
                    <span>Conteos: <span className="text-gray-300">{Object.entries(detail.counts).map(([k, v]) => `${k}=${v}`).join(', ')}</span></span>
                  )}
                </div>
                {detail.error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-xs font-bold text-red-400 mb-1 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> {detail.error}</p>
                    {detail.traceback && (
                      <pre className="text-[11px] text-red-300/80 whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto font-mono">{detail.traceback}</pre>
                    )}
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-bold uppercase text-gray-500">Log de la ejecución</p>
                    <button onClick={() => { navigator.clipboard?.writeText(detail.log || ''); toast.success('Log copiado'); }}
                      className="text-[10px] font-bold text-gray-500 hover:text-gray-300">Copiar</button>
                  </div>
                  <pre className="text-[11px] text-gray-300 bg-black/40 border border-gray-800 rounded-lg p-3 whitespace-pre-wrap overflow-x-auto max-h-80 overflow-y-auto font-mono">
{detail.log || '(sin log)'}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">Sin detalle.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ── Página ───────────────────────────────────────────────────────────────────
const TareasProgramadasPage = () => {
  const { token } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [fProcess, setFProcess] = useState('');
  const [fStatus, setFStatus] = useState('');

  const fetchConfig = useCallback(async () => {
    setLoadingCfg(true);
    try {
      const resp = await fetch(BASE, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || 'Error al cargar configuración');
      setProcesses(d.processes || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingCfg(false); }
  }, [token]);

  const fetchRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const params = new URLSearchParams();
      if (fProcess) params.set('process', fProcess);
      if (fStatus) params.set('status', fStatus);
      params.set('limit', '100');
      const resp = await fetch(`${BASE}/runs?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || 'Error al cargar historial');
      setRuns(d.runs || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingRuns(false); }
  }, [token, fProcess, fStatus]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  return (
    <div className="text-gray-200 min-h-screen font-sans">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent flex items-center gap-3">
              <Clock className="w-7 h-7 text-[#00a1df]" /> Tareas Programadas
            </h1>
            <p className="text-gray-500 text-sm mt-1">Horarios automáticos de procesos (zona horaria: Argentina)</p>
          </div>
          <button onClick={() => { fetchConfig(); fetchRuns(); }}
            className="p-2.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all" title="Recargar">
            <RefreshCw className={`w-4 h-4 ${(loadingCfg || loadingRuns) ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Tarjetas de configuración */}
        {loadingCfg ? (
          <div className="py-12 flex justify-center text-gray-600"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {processes.map(p => (
              <ProcessCard key={p.process} process={p.process} data={p} token={token}
                onSaved={() => { fetchConfig(); }} onRan={() => { fetchConfig(); fetchRuns(); }} />
            ))}
          </div>
        )}

        {/* Historial de ejecuciones (logs) */}
        <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
          <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#00a1df]" /> Historial de ejecuciones
            </h3>
            <div className="flex items-center gap-2">
              <select value={fProcess} onChange={e => setFProcess(e.target.value)}
                className="bg-[#0F1117] border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-[#00a1df]">
                <option value="">Todos los procesos</option>
                <option value="yiqi-import">Importación YIQI</option>
                <option value="aprobacion-cotizaciones">Aprobación de Cotizaciones</option>
                <option value="ordenes-compra">Órdenes de Compra</option>
                <option value="conciliacion-arca">Conciliación ARCA</option>
                <option value="cartera">Cartera (deuda y cheques)</option>
                <option value="panel-diario-snapshot">Snapshot del Panel Diario</option>
              </select>
              <select value={fStatus} onChange={e => setFStatus(e.target.value)}
                className="bg-[#0F1117] border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-[#00a1df]">
                <option value="">Todos los estados</option>
                <option value="ok">OK</option>
                <option value="partial">Parcial</option>
                <option value="error">Error</option>
                <option value="running">En curso</option>
              </select>
            </div>
          </div>

          {loadingRuns ? (
            <div className="py-16 flex flex-col items-center gap-3 text-gray-600">
              <Loader2 className="w-7 h-7 animate-spin" /><p className="text-sm">Cargando historial...</p>
            </div>
          ) : runs.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-3 text-gray-600">
              <CheckCircle2 className="w-7 h-7" /><p className="text-sm">No hay ejecuciones todavía</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0F1117] border-b border-gray-800 text-gray-500 uppercase text-[10px] font-black tracking-widest">
                    <th className="px-4 py-3 w-8"></th>
                    <th className="px-4 py-3 text-left">Fecha y hora</th>
                    <th className="px-4 py-3 text-left">Proceso</th>
                    <th className="px-4 py-3 text-left">Disparo</th>
                    <th className="px-4 py-3 text-left">Cron</th>
                    <th className="px-4 py-3 text-left">Estado</th>
                    <th className="px-4 py-3 text-left">Resumen</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => <RunRow key={run.id} run={run} token={token} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TareasProgramadasPage;
